import { useEffect, useState } from "react";
import { AlertTriangle, RotateCcw, ShieldAlert, ArrowRight } from "lucide-react";

// Étapes RÉELLES du pipeline V2 (interprétation profil -> construction cycle -> vérification).
// On ne prétend PAS connaître le pourcentage exact : barre indéterminée + libellés honnêtes,
// aucun faux "73 %".
const STEPS = [
  "Analyse de ton profil",
  "MAI construit ton cycle",
  "Vérification de ton programme",
];

export function ProgramGenerating({ preview } = {}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % STEPS.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="fade-up min-h-[70vh] flex flex-col items-center justify-center text-center px-6"
      data-testid="program-generating">
      <style>{`
        @keyframes maiIndeterminate {
          0% { left: -40%; width: 40%; }
          50% { width: 55%; }
          100% { left: 100%; width: 40%; }
        }
        @keyframes maiBreath {
          0%,100% { transform: scale(1); opacity: .55; }
          50% { transform: scale(1.35); opacity: .12; }
        }
      `}</style>

      {/* Marque MAI animée (respiration sobre, cohérente identité sage/obsidian) */}
      <div className="relative flex items-center justify-center h-24 w-24">
        <span className="absolute inline-flex h-16 w-16 rounded-full bg-sage"
          style={{ animation: "maiBreath 2.6s ease-in-out infinite" }} />
        <span className="absolute inline-flex h-24 w-24 rounded-full border border-sage/25"
          style={{ animation: "maiBreath 2.6s ease-in-out infinite", animationDelay: ".4s" }} />
        <span className="relative text-2xl font-light tracking-[0.3em] text-obsidian pl-[0.3em]">MAI</span>
      </div>

      <h1 className="mt-8 text-3xl font-light tracking-tight text-obsidian">MAI construit ton programme</h1>
      <p className="mt-4 max-w-md text-obsidian/60 leading-relaxed">
        Ton bilan est terminé. MAI analyse maintenant ton profil et construit ton premier cycle d'entraînement.
      </p>

      {/* Barre de progression INDÉTERMINÉE (animation honnête, pas de faux pourcentage) */}
      <div className="mt-8 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-obsidian/[0.08]"
        role="progressbar" aria-label="Génération en cours" data-testid="program-generating-bar">
        <div className="relative h-full w-full">
          <span className="absolute top-0 h-full rounded-full bg-sage"
            style={{ animation: "maiIndeterminate 1.6s ease-in-out infinite" }} />
        </div>
      </div>

      <p className="mt-5 text-sm tracking-[0.14em] uppercase font-semibold text-sage transition-opacity"
        data-testid="program-generating-step">
        {STEPS[i]}…
      </p>
      <p className="mt-3 text-xs text-obsidian/40">Cela peut prendre quelques instants. Tu peux quitter cette page, MAI continue.</p>

      {preview && (
        <div className="mt-10 w-full max-w-md text-left rounded-2xl border border-border bg-white p-5"
          data-testid="generating-preview">
          <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-sage">
            Ta préparation, dans les grandes lignes
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] tracking-wide uppercase text-obsidian/45 font-semibold">Objectif</div>
              <div className="mt-1 text-sm font-medium text-obsidian leading-snug" data-testid="preview-objectif">{preview.objectif || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-wide uppercase text-obsidian/45 font-semibold">Durée</div>
              <div className="mt-1 text-sm font-medium text-obsidian leading-snug" data-testid="preview-duree">{preview.weeks_total ? `${preview.weeks_total} sem.` : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-wide uppercase text-obsidian/45 font-semibold">Rythme</div>
              <div className="mt-1 text-sm font-medium text-obsidian leading-snug" data-testid="preview-rythme">{preview.frequency ? `${preview.frequency}×/sem` : "—"}</div>
            </div>
          </div>
          {preview.orientation && (
            <p className="mt-4 text-sm text-obsidian/70 leading-relaxed" data-testid="preview-orientation">{preview.orientation}</p>
          )}
          {Array.isArray(preview.axes_progression) && preview.axes_progression.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="preview-axes">
              {preview.axes_progression.slice(0, 3).map((p, k) => (
                <li key={k} className="flex gap-2 text-sm text-obsidian/70 leading-relaxed">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sage shrink-0" />{p}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ProgramGenState({ variant, onRetry }) {
  if (variant === "blocked") {
    return (
      <div className="fade-up min-h-[70vh] flex flex-col items-center justify-center text-center px-6"
        data-testid="program-gen-blocked">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
          <ShieldAlert size={28} strokeWidth={1.5} />
        </div>
        <h1 className="mt-6 text-2xl font-light tracking-tight">On préfère prendre le temps de bien faire</h1>
        <p className="mt-4 max-w-md text-obsidian/60 leading-relaxed">
          MAI n'a pas pu construire un programme totalement sûr à partir de ton profil actuel. Ton coach en est informé
          et reviendra vers toi. Tu peux aussi compléter ton bilan pour affiner ta préparation.
        </p>
        <button data-testid="program-gen-blocked-bilan" onClick={onRetry}
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-obsidian px-6 py-3 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">
          Compléter mon bilan <ArrowRight size={15} />
        </button>
      </div>
    );
  }

  if (variant === "needs_bilan") {
    return (
      <div className="fade-up min-h-[70vh] flex flex-col items-center justify-center text-center px-6"
        data-testid="program-gen-needs-bilan">
        <h1 className="text-2xl font-light tracking-tight">Il manque quelques infos</h1>
        <p className="mt-4 max-w-md text-obsidian/60 leading-relaxed">
          MAI a besoin d'un peu plus d'éléments sur toi pour construire une préparation pertinente. Complète ton bilan,
          ça ne prend qu'une minute.
        </p>
        <button data-testid="program-gen-needs-bilan-cta" onClick={onRetry}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
          Compléter mon bilan <ArrowRight size={15} />
        </button>
      </div>
    );
  }

  // error
  return (
    <div className="fade-up min-h-[70vh] flex flex-col items-center justify-center text-center px-6"
      data-testid="program-gen-error">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle size={28} strokeWidth={1.5} />
      </div>
      <h1 className="mt-6 text-2xl font-light tracking-tight">Impossible de finaliser ton programme</h1>
      <p className="mt-4 max-w-md text-obsidian/60 leading-relaxed">
        Un problème est survenu pendant la génération. Rien n'est perdu — réessaie, MAI repart de ton bilan.
      </p>
      <button data-testid="program-gen-retry" onClick={onRetry}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform">
        <RotateCcw size={15} /> Réessayer
      </button>
    </div>
  );
}
